import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Calendar, Shield, Activity, Key } from 'lucide-react';
import { Card, Button, Badge } from '@/components/common';
import { userService } from '@/services/userService';
import { rbacService } from '@/services/rbacService';
import { formatDate, getInitials } from '@/utils/format';
import { useToast } from '@/contexts/ToastContext';
import type { User, Role } from '@/types';

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);

  useEffect(() => {
    if (id) {
      fetchUser();
      fetchRoles();
    }
  }, [id]);

  const fetchUser = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const userData = await userService.getUser(id);
      setUser(userData);
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to load user', 'error');
      navigate('/users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await rbacService.getRoles({ per_page: 50 });
      setAvailableRoles(response.data);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  };

  const handleToggleRole = async (roleId: string, hasRole: boolean) => {
    if (!user) return;
    try {
      if (hasRole) {
        await userService.removeRole(user.id, roleId);
        showToast('Role removed successfully', 'success');
      } else {
        await userService.assignRole(user.id, roleId);
        showToast('Role assigned successfully', 'success');
      }
      fetchUser();
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to update role', 'error');
    }
  };

  const handleToggleStatus = async () => {
    if (!user) return;
    try {
      await userService.toggleUserStatus(user.id);
      showToast('User status updated', 'success');
      fetchUser();
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to update status', 'error');
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: Shield },
    { id: 'roles', label: 'Roles', icon: Key },
    { id: 'activity', label: 'Activity', icon: Activity },
  ];

  if (loading || !user) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" icon={<ArrowLeft className="w-4 h-4" />} onClick={() => navigate('/users')}>
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-secondary-900">User Details</h1>
        </div>
        <Button onClick={handleToggleStatus}>
          {user.email_verified_at ? 'Suspend User' : 'Activate User'}
        </Button>
      </div>

      {/* User Header Card */}
      <Card>
        <div className="flex items-start gap-6">
          <div className="w-24 h-24 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-3xl font-bold">
            {getInitials(user.name)}
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-secondary-900">{user.name}</h2>
            <div className="flex items-center gap-2 text-secondary-600 mt-1">
              <Mail className="w-4 h-4" />
              {user.email}
            </div>
            <div className="flex items-center gap-3 mt-3">
              <Badge variant={user.email_verified_at ? 'success' : 'warning'}>
                {user.email_verified_at ? 'Verified' : 'Pending'}
              </Badge>
              {user.roles && user.roles.length > 0 && (
                <div className="flex gap-1">
                  {user.roles.map((role) => (
                    <Badge key={role.id} variant="info">
                      {role.name}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 text-sm text-secondary-600">
              <Calendar className="w-4 h-4" />
              Joined {formatDate(user.created_at, 'MMM d, yyyy')}
            </div>
            {user.mfa_enabled && (
              <Badge variant="success" className="mt-2">
                MFA Enabled
              </Badge>
            )}
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-secondary-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
              activeTab === tab.id
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-secondary-600 hover:text-secondary-900'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'profile' && (
        <Card>
          <h3 className="text-lg font-bold mb-4">Profile Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-secondary-600">Name</label>
              <p className="font-medium">{user.name}</p>
            </div>
            <div>
              <label className="text-sm text-secondary-600">Email</label>
              <p className="font-medium">{user.email}</p>
            </div>
            <div>
              <label className="text-sm text-secondary-600">Created</label>
              <p className="font-medium">{formatDate(user.created_at, 'MMM d, yyyy HH:mm')}</p>
            </div>
            <div>
              <label className="text-sm text-secondary-600">Updated</label>
              <p className="font-medium">{formatDate(user.updated_at, 'MMM d, yyyy HH:mm')}</p>
            </div>
            <div>
              <label className="text-sm text-secondary-600">Email Verified</label>
              <p className="font-medium">
                {user.email_verified_at ? formatDate(user.email_verified_at, 'MMM d, yyyy') : 'Not verified'}
              </p>
            </div>
            <div>
              <label className="text-sm text-secondary-600">MFA Status</label>
              <p className="font-medium">{user.mfa_enabled ? 'Enabled' : 'Disabled'}</p>
            </div>
          </div>
        </Card>
      )}

      {activeTab === 'roles' && (
        <Card>
          <h3 className="text-lg font-bold mb-4">Role Management</h3>
          <div className="space-y-2">
            {availableRoles.map((role) => {
              const hasRole = user.roles?.some((r) => r.id === role.id);
              return (
                <div key={role.id} className="flex items-center justify-between p-3 border border-secondary-200 rounded-lg">
                  <div>
                    <p className="font-medium">{role.name}</p>
                    <p className="text-sm text-secondary-600">{role.description || role.slug}</p>
                  </div>
                  <Button
                    variant={hasRole ? 'danger' : 'primary'}
                    onClick={() => handleToggleRole(role.id, hasRole || false)}
                  >
                    {hasRole ? 'Remove' : 'Assign'}
                  </Button>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {activeTab === 'activity' && (
        <Card>
          <h3 className="text-lg font-bold mb-4">Activity History</h3>
          <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-8 text-center">
            <Activity className="w-12 h-12 text-secondary-400 mx-auto mb-3" />
            <p className="text-secondary-600">Activity tracking will be displayed here</p>
            <p className="text-sm text-secondary-500 mt-1">Login history, actions, and events</p>
          </div>
        </Card>
      )}
    </div>
  );
}
